import { isPlatformBrowser } from '@angular/common';
import { computed, effect, inject, PLATFORM_ID } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { AppDimension } from 'common-ui';
import { withAppBreakpoints } from '../features/withAppBreakpoints';
import { AppPopoverStore } from './app-popover.store';
import { withPersistence } from '../features/withPersistence';

export interface AppMenuState {
  _opened: boolean;
  /** When true, the menu is not closed automatically when the next navigation completes. */
  _keepOpenOnNavigation: boolean;
}

export const AppMenuStore = signalStore(
  { providedIn: 'root' },
  withState<AppMenuState>(() => ({
    _opened: false,
    _keepOpenOnNavigation: false,
  })),
  withAppBreakpoints(),
  withComputed(({ _opened: opened, breakpointWidth: breakpointWidth }) => {
    const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
    const isInline = computed(() => {
      if (!isBrowser) return false;
      const appWidthBp = breakpointWidth();
      if (appWidthBp > AppDimension.md) {
        return true;
      }
      return false;
    });
    const isOpen = computed(() => isInline() || opened());
    return {
      isInline,
      isOpen,
    };
  }),
  withMethods((store) => {
    const popoverStore = inject(AppPopoverStore);
    const open = () => {
      popoverStore.setActive('menu');
      patchState(store, { _opened: true });
    };
    const close = () => patchState(store, { _opened: false });
    const toggle = () => (store._opened() ? close() : open());
    const keepOpenOnNextNavigation = () => patchState(store, { _keepOpenOnNavigation: true });
    /** Returns whether keep-open was requested for the navigation that just finished, and clears the request. */
    const consumeKeepOpenOnNavigation = () => {
      const keepOpen = store._keepOpenOnNavigation();
      patchState(store, { _keepOpenOnNavigation: false });
      return keepOpen;
    };

    return {
      open,
      close,
      toggle,
      keepOpenOnNextNavigation,
      consumeKeepOpenOnNavigation,
    };
  }),
  withHooks((store) => {
    let prevWidth: number | undefined = undefined;
    return {
      onInit: () => {
        effect(() => {
          const breakpointWidth = store.breakpointWidth();
          if (breakpointWidth > AppDimension.md) {
            patchState(store, { _opened: true });
          } else if (
            prevWidth != undefined &&
            prevWidth !== breakpointWidth &&
            prevWidth > AppDimension.md &&
            breakpointWidth <= AppDimension.md
          ) {
            patchState(store, { _opened: false });
          }
          prevWidth = breakpointWidth;
        });
      },
    };
  }),
  withPersistence({
    key: 'idealsupply.ideallink.app-shell.menu',
    persist: (store) => ({
      _opened: store._opened(),
    }),
    restore: (store, data) => {
      patchState(store, { _opened: data._opened });
    },
  }),
);
